import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import {
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import useParemeterLessApi from "../../hooks/useParemeterLessApi";
import useApi from "../../hooks/useApi";
import { useToaster } from "../../hooks/useToaster";
import { useMenu } from "../../context/MenuContext";
import { useUser } from "../../context/UserContext";
import { ModuleNames, POSTIF_STATIC_UI_BYPASS, SEVERITIES } from "../../data/constants";
import { getMyPostInterviewForms, getPostInterviewForms, markPostIfAsEmployee, softDeletePostIf } from "../../services/pifService";
import { PifListItem, PifListStatus } from "../../pages/supervision/PostInterviewForm/types";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import PostIFMarkEmployeeDialog from "./PostIFMarkEmployeeDialog";
import PostIFTable, { PostIFTableVariant } from "./PostIFTable";
import PostIFDetailDialog from "./PostIFDetailDialog";
import PifBreadcrumbs from "../../pages/supervision/PostInterviewForm/PifBreadcrumbs";
import "./PostInterviewForms.scss";

const ALL = "all";
const NEW_FORM_URL = "/supervision/postif/new";

// MUI centres a Select's menu on its field by default, so options longer than the field
// (e.g. "Comfort Health — CCM") spill out both sides and drift over the next control.
const SELECT_MENU_PROPS = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
} as const;

// Display order for the status filter. The options actually shown are derived from the rows the
// API returns (see `statusOptions` below); this list only ranks them so the dropdown reads
// DRAFT → pending stages → APPROVED/DECLINED rather than alphabetically.
const STATUS_ORDER: PifListStatus[] = [
  "DRAFT",
  "RETURNED",
  "PENDING DEPT DIR",
  "PENDING HR DIR",
  "PENDING EXEC DIR",
  "APPROVED",
  "DECLINED",
];

// Permissions decide the grid in a real session, but the static-UI bypass leaves them empty,
// so "?view=hr" / "?view=my" lets both grids be reviewed before the modules are seeded.
const readVariantOverride = (value: string | null): PostIFTableVariant | null => {
  if (!POSTIF_STATIC_UI_BYPASS) return null;
  return value === "hr" || value === "my" ? value : null;
};

const PostInterviewForms: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { permissions } = useMenu();
  const { userId } = useUser();
  const { parameterlessapiloading: loading, parameterlessexecuteApi: executeApi } =
    useParemeterLessApi();
  const { executeApi: executeMutation } = useApi();
  const showToaster = useToaster();
  const [allRows, setAllRows] = useState<PifListItem[]>([]);
  const [searchText, setSearchText] = useState("");
  const [department, setDepartment] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [detailRow, setDetailRow] = useState<PifListItem | null>(null);
  const [deleteRow, setDeleteRow] = useState<PifListItem | null>(null);
  const [markRow, setMarkRow] = useState<PifListItem | null>(null);

  const canViewHrList = Boolean(
    permissions.find((permission) => permission.moduleName === ModuleNames.PostIFList)?.canView,
  );
  // Hiring supervisor view unless the user has the HR list grant. Empty permissions
  // (static-UI bypass) fall through to the supervisor grid so local work still has a table.
  const variant: PostIFTableVariant =
    readVariantOverride(searchParams.get("view")) ?? (canViewHrList ? "hr" : "my");

  // "My" and "HR" are separate scoped endpoints (my-list / hr-list); the server decides the
  // scope, so we call the one matching the grid variant rather than filtering client-side.
  const loadList = useCallback(async () => {
    const fetchList =
      variant === "hr" ? () => getPostInterviewForms(userId) : () => getMyPostInterviewForms(userId);
    const data = await executeApi(fetchList);
    if (data) {
      setAllRows(data);
    }
  }, [executeApi, variant, userId]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const departments = useMemo(
    () => Array.from(new Set(allRows.map((row) => row.department))).sort((a, b) => a.localeCompare(b)),
    [allRows],
  );

  // Only the statuses actually present in the loaded rows, ordered by STATUS_ORDER.
  const statusOptions = useMemo(() => {
    const present = Array.from(new Set(allRows.map((row) => row.status)));
    const rank = (value: PifListStatus) => {
      const index = STATUS_ORDER.indexOf(value);
      return index === -1 ? STATUS_ORDER.length : index;
    };
    return present.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  }, [allRows]);

  const visibleRows = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return allRows.filter((row) => {
      if (department !== ALL && row.department !== department) return false;
      if (status !== ALL && row.status !== status) return false;
      if (!search) return true;

      return [row.title, row.applicant, row.position].some((field) =>
        field.toLowerCase().includes(search),
      );
    });
  }, [allRows, department, status, searchText]);

  const handleNewForm = () => navigate(NEW_FORM_URL);

  // Carry the ?view= override onto the detail routes so their "Back to list" returns to the
  // same grid — without it the list falls back to permissions and shows the supervisor view.
  const listSearch = searchParams.toString();
  const withListSearch = (path: string) => (listSearch ? `${path}?${listSearch}` : path);

  const handleOpenFromDetail = (row: PifListItem) => {
    setDetailRow(null);
    // "Open form" only OPENS the record - it never starts an edit, on either grid. The plain
    // /:id route is the read-only approval screen (sign buttons appear only when it's the
    // viewer's turn), so a draft or a returned form is shown as-is rather than reopened in the
    // editor. Editing stays on the pencil action in PostIFTable, which is already gated to the
    // rows the viewer may actually mutate; routing an editable row here too meant one click from
    // the lightbox silently put the form back into edit mode.
    navigate(withListSearch(`/supervision/postif/${row.id}`));
  };

  // HR clicks "Mark as employee" → collect the Enterprise employee number, then call the API and
  // refetch (mark-as-employee stamps EnterpriseEmployeeId, which drops the row off the HR list).
  const handleConfirmMark = async (enterpriseEmployeeId: number) => {
    if (!markRow) return;
    const result = await executeMutation(markPostIfAsEmployee, {
      postIfId: Number(markRow.id),
      enterpriseEmployeeId,
    });
    if (result) {
      showToaster("Marked as employee.", SEVERITIES.SUCCESS);
      await loadList();
    }
    setMarkRow(null);
  };

  const handleConfirmDelete = async (confirmed: boolean) => {
    if (confirmed && deleteRow) {
      // useApi surfaces any business failure (403 not-owner, 409 already-submitted, …) as a
      // Redux error alert and returns null, so a non-null result means the delete succeeded.
      const result = await executeMutation(softDeletePostIf, Number(deleteRow.id));
      if (result) {
        showToaster("Draft deleted.", SEVERITIES.SUCCESS);
        await loadList();
      }
    }
    setDeleteRow(null);
  };

  const handleDepartmentChange = (event: SelectChangeEvent<string>) => {
    setDepartment(event.target.value);
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setStatus(event.target.value);
  };

  return (
    <div className="postif-page">
      <header className="postif-page__header">
        <div>
          {/* Inside the banner, not above it: the header bleeds edge-to-edge with margin:-15px,
              which would slide up over a breadcrumb placed before it. Hence the onDark tone. */}
          <PifBreadcrumbs tone="onDark" />
          <h1 className="postif-page__title">Post Interview Forms</h1>
          <p className="postif-page__subtitle">Employee Portal · Hiring</p>
        </div>
      </header>

      <section className="postif-card">
        <div className="postif-card__toolbar">
          <TextField
            className="postif-card__search-wrap"
            size="small"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search title, applicant, position…"
            aria-label="Search title, applicant or position"
            data-testid="postif-search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" aria-hidden />
                </InputAdornment>
              ),
            }}
          />

          <FormControl className="postif-card__select" size="small">
            <InputLabel id="postif-department-label">Department</InputLabel>
            <Select
              labelId="postif-department-label"
              id="postif-department-select"
              label="Department"
              value={department}
              onChange={handleDepartmentChange}
              MenuProps={SELECT_MENU_PROPS}
              data-testid="postif-filter-dept"
            >
              <MenuItem value={ALL}>All departments</MenuItem>
              {departments.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className="postif-card__select" size="small">
            <InputLabel id="postif-status-label">Status</InputLabel>
            <Select
              labelId="postif-status-label"
              id="postif-status-select"
              label="Status"
              value={status}
              onChange={handleStatusChange}
              MenuProps={SELECT_MENU_PROPS}
              data-testid="postif-filter-status"
            >
              <MenuItem value={ALL}>All statuses</MenuItem>
              {statusOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {variant === "my" && (
            <Button
              className="postif-card__new"
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleNewForm}
              data-testid="postif-new"
            >
              + New PostIF
            </Button>
          )}
        </div>

        <PostIFTable
          variant={variant}
          rows={visibleRows}
          loading={loading}
          onEdit={(row) => navigate(withListSearch(`/supervision/postif/${row.id}/edit`))}
          onOpenDetail={setDetailRow}
          onOpen={handleOpenFromDetail}
          onDelete={setDeleteRow}
          onMarkAsEmployee={setMarkRow}
        />
      </section>

      <PostIFDetailDialog
        record={detailRow}
        openLabel={variant === "hr" ? "Open" : "Open form"}
        onClose={() => setDetailRow(null)}
        onOpen={handleOpenFromDetail}
      />

      <ConfirmationDialog
        open={Boolean(deleteRow)}
        title="Delete this draft?"
        message={
          deleteRow
            ? `${deleteRow.title} will be removed. This cannot be undone.`
            : ""
        }
        onClose={handleConfirmDelete}
      />

      <PostIFMarkEmployeeDialog
        open={Boolean(markRow)}
        applicantName={markRow?.applicant}
        onClose={() => setMarkRow(null)}
        onConfirm={handleConfirmMark}
      />
    </div>
  );
};

export default PostInterviewForms;
